import { useState, type ReactElement, type ReactNode } from 'react';
import { useMessages } from '../i18n/locale';
import type { Tone } from '../lib/status';
import { EmptyState, secondaryButtonStyle } from './primitives';

/**
 * Таблица рабочего места (разд. 14, 4.4), в виде таблиц панели доставки: карточка,
 * заголовки колонок мелкими заглавными, разделители строк, подсветка при наведении и
 * цветная полоска слева — состояние строки читается, не доходя до колонки статуса.
 *
 * Сохранены свойства, ради которых она написана своя:
 *
 * * **курсорная пагинация кнопкой «Ещё»**, а не страницы и не бесконечный скролл:
 *   при живом потоке заказы «прыгают» между страницами, а диспетчеру нужно уметь
 *   вернуться на то же место;
 * * **живые обновления без прыжков** — новые строки не переставляют список под
 *   курсором.
 */

export interface Column<T> {
  readonly key: string;
  readonly title: string;
  readonly width?: string | undefined;
  readonly align?: 'left' | 'right' | undefined;
  readonly render: (row: T) => ReactNode;
}

const RAIL: Record<Tone, string> = {
  danger: 'var(--danger-rail)',
  warn: 'var(--warning-rail)',
  wait: 'var(--warning-rail)',
  ok: 'var(--success)',
  progress: 'var(--accent-line)',
  neutral: 'transparent',
};

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  dense = false,
  onRowClick,
  selectedKey,
  hasMore = false,
  onMore,
  emptyText,
  onReset,
  resetLabel,
  highlightKeys,
  rail,
}: {
  readonly rows: readonly T[];
  readonly columns: readonly Column<T>[];
  readonly rowKey: (row: T) => string;
  readonly dense?: boolean | undefined;
  readonly onRowClick?: ((row: T) => void) | undefined;
  readonly selectedKey?: string | null | undefined;
  readonly hasMore?: boolean | undefined;
  readonly onMore?: (() => void) | undefined;
  readonly emptyText: string;
  readonly onReset?: (() => void) | undefined;
  readonly resetLabel?: string | undefined;
  /** Строки, изменившиеся только что: подсветка на полторы секунды (разд. 4.4). */
  readonly highlightKeys?: ReadonlySet<string> | undefined;
  /** Тон полоски слева у строки; не задан — полоски нет. */
  readonly rail?: ((row: T) => Tone) | undefined;
}): ReactElement {
  const messages = useMessages();
  const [hovered, setHovered] = useState<string | null>(null);

  const card = {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-lg)',
    boxShadow: 'var(--shadow-card)',
    overflow: 'hidden',
  } as const;

  if (rows.length === 0) {
    return (
      <section style={card}>
        <EmptyState
          text={emptyText}
          {...(onReset === undefined ? {} : { onReset })}
          {...(resetLabel === undefined ? {} : { resetLabel })}
        />
      </section>
    );
  }

  const height = dense ? 'var(--row-h-dense)' : 'var(--row-h)';

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      <section style={{ ...card, overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  style={{
                    textAlign: column.align ?? 'left',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 'var(--fw-bold)',
                    letterSpacing: 'var(--ls-caps)',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                    padding: `var(--sp-3) var(--sp-3) var(--sp-2) ${index === 0 ? 'var(--sp-4)' : 'var(--sp-3)'}`,
                    width: column.width ?? 'auto',
                    position: 'sticky',
                    top: 0,
                    background: 'var(--surface)',
                  }}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const key = rowKey(row);
              const selected = key === selectedKey;
              const tone = rail?.(row) ?? 'neutral';

              return (
                <tr
                  key={key}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  onMouseEnter={() => setHovered(key)}
                  onMouseLeave={() => setHovered((current) => (current === key ? null : current))}
                  tabIndex={onRowClick === undefined ? undefined : 0}
                  onKeyDown={(event) => {
                    if (onRowClick !== undefined && event.key === 'Enter') {
                      onRowClick(row);
                    }
                  }}
                  style={{
                    cursor: onRowClick === undefined ? 'default' : 'pointer',
                    background: selected
                      ? 'var(--accent-soft)'
                      : highlightKeys?.has(key) === true
                        ? 'var(--warning-soft)'
                        : hovered === key
                          ? 'var(--surface-muted)'
                          : 'transparent',
                    transition: 'background var(--motion)',
                  }}
                >
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      style={{
                        height,
                        padding: `var(--sp-2) var(--sp-3) var(--sp-2) ${index === 0 ? 'var(--sp-4)' : 'var(--sp-3)'}`,
                        borderTop: '1px solid var(--line)',
                        textAlign: column.align ?? 'left',
                        verticalAlign: 'middle',
                        lineHeight: 1.3,
                        maxWidth: column.width ?? 'none',
                        boxShadow:
                          index === 0 ? `inset var(--rail-w) 0 0 ${RAIL[tone]}` : undefined,
                      }}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        <span className="num">{`${messages.cabinet.flow.shown} ${rows.length}`}</span>
        {hasMore && onMore !== undefined ? (
          <button type="button" onClick={onMore} style={secondaryButtonStyle}>
            {messages.cabinet.flow.more}
          </button>
        ) : null}
      </div>
    </div>
  );
}
